// Display helpers shared by the report surface. Pure, leaf, and deliberately
// tiny — the report models arrive fully derived, so nothing here computes a
// figure; these only decide how an already-computed value is rendered and
// toned.
//
// The one arithmetic exception is the trend sentence, which subtracts two
// rates the server has already decided are safe to state. It refuses to
// compare when either side is absent, so a withheld rate can never reappear
// as an implied zero.

import type { MetricCardTone } from "../tracking/deliverable/MetricCard";
import { humaniseDays } from "../../../utils/formatCoverage";
import {
  formatMonthYear,
  formatMonthYearShort,
} from "../../../utils/formatShortDate";
import type { ReportMonth, ReportRange } from "../../../types/report";

/** A percentage, or an em dash when the server had no value to give.
 *  A missing measurement must never render as 0% — that would invent a
 *  reading the data does not support. */
export function formatPercent(value: number | null): string {
  if (value === null) return "—";
  return `${Math.round(value)}%`;
}

/**
 * A compliance percentage at the precision the server sent it.
 *
 * Separate from `formatPercent` on purpose: compliance is rounded once, to
 * one decimal, by the canonical aggregation, and rounding it again here would
 * put 42% on the report beside 41.7% on the KPI tab for the same contract.
 */
export function formatCompliancePercent(value: number | null): string {
  if (value === null) return "—";
  return `${value}%`;
}

/** The zero-count tone rule: a count of problems takes its alarm tone only
 *  when there is actually something to alarm about. A clean dashboard full of
 *  red zeroes trains the reader to ignore the colour everywhere else. */
export function countTone(count: number, alarm: MetricCardTone): MetricCardTone {
  return count > 0 ? alarm : "neutral";
}

/** Cadence-health tone, threshold-driven rather than count-driven: a high
 *  on-time rate is reassuring and stays visibly positive at 100%. */
export function onTimeRateTone(rate: number | null): MetricCardTone {
  if (rate === null) return "neutral";
  if (rate >= 90) return "success";
  if (rate >= 70) return "neutral";
  return "warning";
}

/**
 * Humanised expiry proximity for a compliance policy, from the server's
 * signed `daysToExpiry` (expiry minus today, so negative once lapsed).
 *
 * The sign is interpreted here rather than rendered: "in -12 days" is what a
 * raw countdown produces for an expired policy, and on a compliance report
 * that reads as a data fault rather than as a lapse. Wording and the
 * day/week/month scale come from the shared `humaniseDays` helper, so this
 * matches the Insurance tab exactly.
 */
export function expiryMeta(daysToExpiry: number): string {
  if (daysToExpiry < 0) return `Expired ${humaniseDays(-daysToExpiry)} ago`;
  return `Expires in ${humaniseDays(daysToExpiry)}`;
}

/** A month key rendered in full, e.g. "Aug 2026". The key is `YYYY-MM`, which
 *  the date formatters cannot parse on its own, so it is anchored to the
 *  first of the month — the day is never displayed. */
export function formatReportMonth(month: ReportMonth): string {
  return formatMonthYear(`${month}-01`);
}

/** A month key abbreviated for a chart axis tick, e.g. "Aug 26". */
export function formatReportMonthShort(month: ReportMonth): string {
  return formatMonthYearShort(`${month}-01`);
}

/** How the preceding window is named in the trend sentence. `all` carries an
 *  entry for completeness only: a contract-to-date range has nothing before
 *  it, so the server sends no comparison rate and no sentence is produced. */
const PRECEDING_WINDOW_LABEL: Record<ReportRange, string> = {
  "1m": "the preceding month",
  "3m": "the preceding 3 months",
  "6m": "the preceding 6 months",
  "12m": "the preceding 12 months",
  all: "the preceding window",
};

/**
 * The movement between a rate and the same-length preceding window, in
 * percentage points. Null when either rate is absent — the server withholds a
 * rate whose sample cannot support it, and treating that as zero would
 * manufacture a dramatic trend out of missing data.
 */
export function formatRateTrend(
  current: number | null,
  previous: number | null,
  range: ReportRange,
): string | null {
  if (current === null || previous === null) return null;
  const label = PRECEDING_WINDOW_LABEL[range];
  const delta = Math.round(current - previous);
  if (delta === 0) return `Level with ${label}`;
  const size = Math.abs(delta);
  const unit = size === 1 ? "point" : "points";
  const direction = delta > 0 ? "better" : "worse";
  return `${size} ${unit} ${direction} than ${label}`;
}
